import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui';
import { getStatusColor, formatStatus, formatDate, formatCurrency } from '../../../../utils/formatters/statusFormatters';
import DeliveryPod from './DeliveryPod';
import { MilestonesSection } from './MilestonesSection';
import { SoWSection } from './SoWSection';
import { FundingRequestsSection } from './FundingRequestsSection';
import { jiraAPI, projectAPI } from '../../../../services/realApi';
import { FiEdit2 as Pencil, FiCheck as Check, FiX as X } from 'react-icons/fi';
import toast from 'react-hot-toast';
import { TransformedProject, Milestone } from '../hooks/useProjects';
import ProjectTaggingSection from './ProjectTaggingSection';

interface Props {
  project: TransformedProject | null;
  onUpdateStatus: (status: string) => void;
  onAddMilestone: () => void;
  onEditMilestone: (milestone: Milestone) => void;
  onDeleteMilestone: (milestone: Milestone) => void;
  onExport: () => void;
  onDelete: () => void;
  onRefresh?: () => void;
}

export const ProjectDetails: React.FC<Props> = ({
  project,
  onUpdateStatus,
  onAddMilestone,
  onEditMilestone,
  onDeleteMilestone,
  onExport,
  onDelete,
  onRefresh
}) => {
  const [showStatusSelect, setShowStatusSelect] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [isSavingName, setIsSavingName] = useState(false);

  if (!project) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-gray-500 dark:text-gray-400">Select a project to view details</p>
      </div>
    );
  }

  const calculatePercentage = (spent: number, budget: number): number => {
    return budget > 0 ? Math.round((spent / budget) * 100) : 0;
  };

  const calculateMilestoneProgress = (): number => {
    const milestones = project.milestones ?? [];
    if (milestones.length === 0) return 0;

    const completedMilestones = milestones.filter(m =>
      ['COMPLETED', 'Completed'].includes(m.status)
    ).length;

    return Math.round((completedMilestones / milestones.length) * 100);
  };

  const handleStatusChange = (newStatus: string) => {
    onUpdateStatus(newStatus);
    setShowStatusSelect(false);
  };

  const handleSyncJira = async () => {
    setIsSyncing(true);
    try {
      const result = await jiraAPI.sync();
      if (result.success) {
        alert(`Sync completed successfully!\n\n${result.data.message ?? 'Jira data synced'}`);
        if (onRefresh) {
          onRefresh();
        }
      } else {
        alert(`Failed to sync Jira data: ${result.error}`);
      }
    } catch (err: unknown) {
      console.error('Error syncing Jira:', err);
      alert('Failed to sync Jira data. Please try again.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleStartEditName = () => {
    setEditedName(project.name);
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setEditedName('');
    setIsEditingName(false);
  };

  const handleSaveName = async () => {
    if (!editedName.trim()) {
      toast.error('Project name cannot be empty');
      return;
    }

    if (editedName.trim() === project.name) {
      handleCancelEditName();
      return;
    }

    setIsSavingName(true);
    try {
      const result = await projectAPI.updateProject(project.id, {
        name: editedName.trim()
      });

      if (result.success) {
        toast.success('Project name updated successfully');
        setIsEditingName(false);
        setEditedName('');

        if (onRefresh) {
          onRefresh();
        }
      } else {
        toast.error(result.error ?? 'Failed to update project name');
      }
    } catch (err: unknown) {
      console.error('Error updating project name:', err);
      toast.error('Failed to update project name');
    } finally {
      setIsSavingName(false);
    }
  };

  const handleNameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditName();
    }
  };

  const statuses = ['PLANNING', 'IN_PROGRESS', 'ON_HOLD', 'COMPLETED', 'CANCELLED'];
  const milestoneProgress = calculateMilestoneProgress();

  return (
    <div className="space-y-6">
      {/* Project Overview */}
      <Card>
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            {isEditingName ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editedName}
                  onChange={(e) => setEditedName(e.target.value)}
                  onKeyDown={handleNameKeyDown}
                  className="text-2xl font-bold text-gray-900 dark:text-white bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                  disabled={isSavingName}
                />
                <button
                  onClick={handleSaveName}
                  disabled={isSavingName}
                  className="p-1.5 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Save"
                >
                  <Check className="w-5 h-5" />
                </button>
                <button
                  onClick={handleCancelEditName}
                  disabled={isSavingName}
                  className="p-1.5 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Cancel"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  {project.name}
                </h2>
                <button
                  onClick={handleStartEditName}
                  className="p-1.5 text-gray-400 hover:text-blue-600 dark:text-gray-500 dark:hover:text-blue-400 transition-colors mb-2"
                  title="Edit project name"
                >
                  <Pencil className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
          <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(project.status)}`}>
            {formatStatus(project.status)}
          </span>
        </div>

        {/* Budget Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-6">
          {/* Infrastructure Budget */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Infrastructure Budget
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {formatCurrency(project.infrastructureBudget ?? 0, project.currency)}
            </p>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full"
                  style={{
                    width: `${Math.min(calculatePercentage(project.infrastructureSpent ?? 0, project.infrastructureBudget ?? 0), 100)}%`
                  }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {calculatePercentage(project.infrastructureSpent ?? 0, project.infrastructureBudget ?? 0)}%
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Spent: {formatCurrency(project.infrastructureSpent ?? 0, project.currency)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Cloud &amp; AI</p>
          </div>

          {/* Delivery Budget */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Delivery Budget
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {formatCurrency(project.deliveryBudget ?? 0, project.currency)}
            </p>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                <div
                  className="bg-green-600 h-1.5 rounded-full"
                  style={{
                    width: `${Math.min(calculatePercentage(project.deliverySpent ?? 0, project.deliveryBudget ?? 0), 100)}%`
                  }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {calculatePercentage(project.deliverySpent ?? 0, project.deliveryBudget ?? 0)}%
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Spent: {formatCurrency(project.deliverySpent ?? 0, project.currency)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Human Hours</p>
          </div>

          {/* Total Budget */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Total Budget
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {formatCurrency((project.infrastructureBudget ?? 0) + (project.deliveryBudget ?? 0), project.currency)}
            </p>
            <div className="flex items-center gap-2 mb-1">
              <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-1.5">
                <div
                  className="bg-blue-600 h-1.5 rounded-full"
                  style={{
                    width: `${Math.min(calculatePercentage(
                      (project.deliverySpent ?? 0) + (project.infrastructureSpent ?? 0),
                      (project.infrastructureBudget ?? 0) + (project.deliveryBudget ?? 0)
                    ), 100)}%`
                  }}
                />
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {calculatePercentage(
                  (project.deliverySpent ?? 0) + (project.infrastructureSpent ?? 0),
                  (project.infrastructureBudget ?? 0) + (project.deliveryBudget ?? 0)
                )}%
              </span>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Computed</p>
          </div>

          {/* Total Spent */}
          <div>
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">
              Spent
            </p>
            <p className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              {formatCurrency((project.deliverySpent ?? 0) + (project.infrastructureSpent ?? 0), project.currency)}
            </p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">Used</p>
          </div>
        </div>

        {/* Overall Progress */}
        <div className="mb-6">
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Overall Progress
          </p>
          <div className="flex items-center gap-4">
            <div className="flex-1 bg-gray-200 dark:bg-gray-600 rounded-full h-4">
              <div
                className="bg-blue-600 h-4 rounded-full flex items-center justify-center text-xs text-white font-medium"
                style={{ width: `${milestoneProgress}%` }}
              >
                {milestoneProgress > 10 && `${milestoneProgress}%`}
              </div>
            </div>
            {milestoneProgress <= 10 && (
              <span className="text-sm font-medium text-gray-900 dark:text-white">
                {milestoneProgress}%
              </span>
            )}
          </div>
        </div>

        {/* Dates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Start Date: </span>
            <span className="text-sm text-gray-900 dark:text-white">{formatDate(project.startDate)}</span>
          </div>
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">End Date: </span>
            <span className="text-sm text-gray-900 dark:text-white">{formatDate(project.endDate)}</span>
          </div>
        </div>
      </Card>

      {/* Project Tagging */}
      <ProjectTaggingSection
        project={{
          id: project.id,
          name: project.name,
          costTagKey: project.costTagKey,
          costTagValue: project.costTagValue,
          costTrackingEnabled: project.costTrackingEnabled,
        }}
        onRefresh={onRefresh}
      />

      {/* Delivery Pod */}
      <DeliveryPod project={project} />

      {/* Milestones */}
      <MilestonesSection
        milestones={project.milestones ?? []}
        onAddMilestone={onAddMilestone}
        onEditMilestone={onEditMilestone}
        onDeleteMilestone={onDeleteMilestone}
      />

      {/* Statement of Work */}
      <SoWSection
        project={project}
        onUpdate={onRefresh}
      />

      {/* Funding Requests */}
      <FundingRequestsSection project={project} />

      {/* Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {showStatusSelect ? (
              <div>
                <span className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Select New Status
                </span>
                <div className="flex gap-2 flex-wrap">
                  {statuses.map(status => (
                    <button
                      key={status}
                      onClick={() => handleStatusChange(status)}
                      className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(status)}`}
                    >
                      {formatStatus(status)}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowStatusSelect(false)}
                    className="px-3 py-1 text-sm font-medium text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-3 flex-wrap">
                <button
                  onClick={() => setShowStatusSelect(true)}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                  Update Status
                </button>
                {project.jiraConfigured && (
                  <button
                    onClick={handleSyncJira}
                    disabled={isSyncing}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-md hover:bg-green-700 dark:bg-green-500 dark:hover:bg-green-600 disabled:bg-green-400 disabled:cursor-not-allowed flex items-center gap-2"
                    title="Sync milestones/sprints from Jira"
                  >
                    <span className={isSyncing ? 'animate-spin' : ''}>⟳</span>
                    {isSyncing ? 'Syncing...' : 'Sync Jira Milestones'}
                  </button>
                )}
                <button
                  onClick={onExport}
                  className="px-4 py-2 text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Export Report
                </button>
                <button
                  onClick={onDelete}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
                >
                  Delete Project
                </button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ProjectDetails;
